import { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Stack,
  Group,
  Title,
  Text,
  Button,
  Loader,
  Center,
  Table,
  Badge,
  Avatar,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useDisclosure } from '@mantine/hooks';
import { Edit2, Link as LinkIcon, X } from 'react-feather';
import { rateCardApi } from '../../../services/api/rateCard';
import { billcomApi } from '../../../services/api/billcom';
import type { RateCard } from '../../../types';
import type { CoachVendorStatus } from '../../../services/api/billcom';
import { RateCardDisplay } from './components/RateCardDisplay';
import { EditRatesModal } from './components/EditRatesModal';
import { ActivityLogDrawer } from './components/ActivityLogDrawer';
import { BillComConnectionStatus } from './components/BillComConnectionStatus';
import { LinkVendorModal } from './components/LinkVendorModal';

export function CompensationSettingsPage() {
  const [rateCard, setRateCard] = useState<RateCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
  const [activityDrawerOpened, { open: openActivityDrawer, close: closeActivityDrawer }] =
    useDisclosure(false);

  // Vendor mapping state
  const [coaches, setCoaches] = useState<CoachVendorStatus[]>([]);
  const [coachesLoading, setCoachesLoading] = useState(false);
  const [linkModalOpened, { open: openLinkModal, close: closeLinkModal }] = useDisclosure(false);
  const [selectedCoach, setSelectedCoach] = useState<CoachVendorStatus | null>(null);

  const fetchRateCard = useCallback(async () => {
    try {
      setLoading(true);
      const data = await rateCardApi.get();
      setRateCard(data);
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to load rate card', color: 'red' });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCoaches = useCallback(async () => {
    try {
      setCoachesLoading(true);
      // Vendor mapping is managed per-cohort on the Coaches → Compensation tab.
      // This section is informational only — no global coach list for vendor mapping.
      setCoaches([]);
    } catch {
      // Silently handle
    } finally {
      setCoachesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRateCard();
  }, [fetchRateCard]);

  const handleRatesUpdated = useCallback(() => {
    closeEditModal();
    fetchRateCard();
    notifications.show({
      title: 'Rates Updated',
      message: 'The global rate card has been updated',
      color: 'green',
    });
  }, [closeEditModal, fetchRateCard]);

  const handleOpenLinkModal = (coach: CoachVendorStatus) => {
    setSelectedCoach(coach);
    openLinkModal();
  };

  const handleUnlink = async (coach: CoachVendorStatus) => {
    try {
      await billcomApi.unlinkVendor(coach.user_id);
      notifications.show({
        title: 'Vendor Unlinked',
        message: `${coach.first_name} ${coach.last_name} unlinked from Bill.com`,
        color: 'blue',
      });
      fetchCoaches();
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to unlink vendor', color: 'red' });
    }
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  const updatedDate = rateCard?.updated_at
    ? new Date(rateCard.updated_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <Container size="xl" py="lg">
      <Stack gap="lg">
        {/* Page Header */}
        <Group justify="space-between" align="center">
          <Title order={3} fw={700}>
            Compensation Settings
          </Title>
          <BillComConnectionStatus />
        </Group>

        {/* Rate Card Section */}
        {rateCard && (
          <Stack
            gap="md"
            p="lg"
            style={{ border: '1px solid #E8E6E1', borderRadius: 8, background: 'white' }}
          >
            {/* Rate Card Header */}
            <Group justify="space-between" align="flex-start">
              <div>
                <Text fw={700} size="lg">
                  Rate Card
                </Text>
                {updatedDate && (
                  <Text size="sm" c="dimmed">
                    Last edited {updatedDate}
                  </Text>
                )}
              </div>
              <Group gap="xs">
                <Button variant="outline" color="gray" bg="white" onClick={openActivityDrawer}>
                  View Activity Log
                </Button>
                <Button leftSection={<Edit2 size={16} />} onClick={openEditModal}>
                  Edit Rates
                </Button>
              </Group>
            </Group>

            {/* Rate Card Tables */}
            <RateCardDisplay rates={rateCard.rates} />
          </Stack>
        )}

        {/* Bill.com Vendor Mapping Section */}
        <Stack
          gap="md"
          p="lg"
          style={{ border: '1px solid #E8E6E1', borderRadius: 8, background: 'white' }}
        >
          <Group justify="space-between" align="center">
            <div>
              <Text fw={700} size="lg">
                Coach &rarr; Bill.com Vendor Mapping
              </Text>
              <Text size="sm" c="dimmed">
                Link coaches to Bill.com vendor records for payment processing
              </Text>
            </div>
          </Group>

          {coachesLoading ? (
            <Center py="lg">
              <Loader size="sm" />
            </Center>
          ) : coaches.length > 0 ? (
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Coach</Table.Th>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Bill.com Vendor</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th w={80}>Action</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {coaches.map((coach) => (
                  <Table.Tr key={coach.user_id}>
                    <Table.Td>
                      <Group gap="sm">
                        <Avatar radius="xl" size="sm" color="gray">
                          {coach.first_name[0]}
                          {coach.last_name[0]}
                        </Avatar>
                        <Text size="sm" fw={500}>
                          {coach.first_name} {coach.last_name}
                        </Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {coach.email}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {coach.billcom_vendor_name || '--'}
                      </Text>
                      {coach.billcom_vendor_id && (
                        <Text size="xs" c="dimmed">
                          {coach.billcom_vendor_id}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {coach.billcom_vendor_id ? (
                        <Badge size="sm" color="medleyGreen" variant="light">
                          Mapped
                        </Badge>
                      ) : (
                        <Badge size="sm" color="warningAmber" variant="light">
                          Unmapped
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {coach.billcom_vendor_id ? (
                        <Tooltip label="Unlink vendor">
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            onClick={() => handleUnlink(coach)}
                          >
                            <X size={14} />
                          </ActionIcon>
                        </Tooltip>
                      ) : (
                        <Button
                          size="compact-xs"
                          variant="light"
                          leftSection={<LinkIcon size={12} />}
                          onClick={() => handleOpenLinkModal(coach)}
                        >
                          Link
                        </Button>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          ) : (
            <Text c="dimmed" ta="center" py="xl" size="sm">
              Vendor mapping is managed per-cohort on the Coaches &rarr; Compensation tab.
              Use the Link Vendor button next to each coach to map them to a Bill.com vendor.
            </Text>
          )}
        </Stack>
      </Stack>

      {/* Edit Rates Modal */}
      {rateCard && (
        <EditRatesModal
          opened={editModalOpened}
          onClose={closeEditModal}
          rateCard={rateCard}
          onSaved={handleRatesUpdated}
        />
      )}

      {/* Activity Log Drawer */}
      <ActivityLogDrawer opened={activityDrawerOpened} onClose={closeActivityDrawer} />

      {/* Link Vendor Modal */}
      {selectedCoach && (
        <LinkVendorModal
          opened={linkModalOpened}
          onClose={closeLinkModal}
          coachUserId={selectedCoach.user_id}
          coachName={`${selectedCoach.first_name} ${selectedCoach.last_name}`}
          coachEmail={selectedCoach.email}
          onLinked={fetchCoaches}
        />
      )}
    </Container>
  );
}

export default CompensationSettingsPage;
